import { format, isValid, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

// ─── helpers ──────────────────────────────────────────────────────────────────

const UPLOAD_ACCEPTED_FILES =
  ".pdf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.json,.ipynb,.py,.jpg,.jpeg,.png,.gif,.webp,.mp4";

function safeDate(val) {
  if (!val) return null;
  const raw = typeof val === "object" && val.date ? val.date : val;
  const d = typeof raw === "string" ? parseISO(raw.replace(" ", "T")) : new Date(raw);
  return isValid(d) ? d : null;
}

function statusColor(status) {
  switch (status) {
    case "live":     return { bg: "bg-red-100",    text: "text-red-700",   dot: "bg-red-500",   label: "Live" };
    case "upcoming": return { bg: "bg-blue-100",   text: "text-blue-700",  dot: "bg-blue-500",  label: "Upcoming" };
    case "recorded": return { bg: "bg-green-100",  text: "text-green-700", dot: "bg-green-500", label: "Recorded" };
    default:         return { bg: "bg-gray-100",   text: "text-gray-600",  dot: "bg-gray-400",  label: "Ended" };
  }
}

function StatusBadge({ status }) {
  const c = statusColor(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${status === "live" ? "animate-pulse" : ""}`} />
      {c.label}
    </span>
  );
}

// ─── flatten all sessions from modules ────────────────────────────────────────

function extractSessions(modules) {
  const out = [];
  for (const mod of modules) {
    for (const s of mod.direct_schedules || []) {
      out.push({ ...s, _module: mod.name });
    }
    for (const week of mod.weeks || []) {
      for (const s of week.schedules || []) {
        out.push({ ...s, _module: mod.name, _week: week.name || `Week ${week.number}` });
      }
    }
  }
  out.sort((a, b) => {
    const da = safeDate(a.start_time) || 0;
    const db = safeDate(b.start_time) || 0;
    return da - db;
  });
  return out;
}

// ─── Session sidebar ──────────────────────────────────────────────────────────

function SessionSidebar({ sessions, selectedId, onSelect, commentCounts }) {
  return (
    <aside className="w-72 flex-shrink-0 flex flex-col bg-white border-r border-gray-100 overflow-y-auto">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
        <p className="font-bold text-gray-800 text-sm">Sessions</p>
        <p className="text-xs text-gray-400 mt-0.5">{sessions.length} total</p>
      </div>
      <div className="flex-1">
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-400 p-6 text-center">No sessions yet</p>
        ) : (
          sessions.map((s) => {
            const c = statusColor(s.status);
            const count = commentCounts[s.id];
            const isSelected = selectedId === s.id;
            return (
              <button
                key={s.id}
                onClick={() => onSelect(s)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors
                  ${isSelected
                    ? "bg-[#001931] text-white"
                    : "hover:bg-gray-50 text-gray-800"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`font-medium text-sm leading-snug line-clamp-2 flex-1 ${isSelected ? "text-white" : ""}`}>
                    {s.topic || "Untitled Session"}
                  </p>
                  {count != null && count > 0 && (
                    <span className={`flex-shrink-0 text-xs font-bold px-1.5 py-0.5 rounded-full
                      ${isSelected ? "bg-white/20 text-white" : "bg-[#d15424]/10 text-[#d15424]"}`}>
                      {count}💬
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold
                    ${isSelected ? "bg-white/15 text-white" : `${c.bg} ${c.text}`}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : c.dot}
                      ${s.status === "live" ? "animate-pulse" : ""}`} />
                    {c.label}
                  </span>
                  {safeDate(s.start_time) && (
                    <span className={`text-xs ${isSelected ? "text-white/70" : "text-gray-400"}`}>
                      {format(safeDate(s.start_time), "MMM d")}
                    </span>
                  )}
                  {s._week && (
                    <span className={`text-xs ${isSelected ? "text-white/60" : "text-gray-400"}`}>
                      · {s._week}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}

// ─── Edit form ────────────────────────────────────────────────────────────────

function MaterialsManager({ session, onChanged }) {
  const materials = session.materials || [];
  const [newName, setNewName] = useState("");
  const [newLink, setNewLink] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef();

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { toast.error("Give the material a name."); return; }
    if (!newLink && !newFile) { toast.error("Add a file or a link."); return; }

    setAdding(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const fd = new FormData();
      fd.append("name", newName);
      if (newLink) fd.append("link", newLink);
      if (newFile) fd.append("file", newFile);

      await client.post(`/schedules/${session.id}/materials`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Material added.");
      setNewName(""); setNewLink(""); setNewFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add material.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (materialId) => {
    setDeletingId(materialId);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.delete(`/class-materials/${materialId}`);
      toast.success("Material removed.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove material.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Class Materials</p>

      {materials.length > 0 && (
        <ul className="space-y-2 mb-4">
          {materials.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <a href={m.url} target="_blank" rel="noreferrer"
                className="text-sm text-[#294a93] hover:underline font-medium truncate">
                {m.name}
              </a>
              <button
                type="button"
                onClick={() => handleDelete(m.id)}
                disabled={deletingId === m.id}
                className="text-xs text-red-500 hover:text-red-700 font-semibold flex-shrink-0 disabled:opacity-50"
              >
                {deletingId === m.id ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Material name, e.g. Week 3 Slides"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
            focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
        />
        <input
          type="url"
          value={newLink}
          onChange={(e) => setNewLink(e.target.value)}
          placeholder="External link (optional), e.g. https://drive.google.com/..."
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
            focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
        />
        <input
          ref={fileRef}
          type="file"
          accept={UPLOAD_ACCEPTED_FILES}
          onChange={(e) => setNewFile(e.target.files[0] || null)}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3
            file:rounded-lg file:border-0 file:text-xs file:font-semibold
            file:bg-[#294a93]/10 file:text-[#294a93] hover:file:bg-[#294a93]/20 cursor-pointer"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding}
          className="w-full bg-[#294a93]/10 hover:bg-[#294a93]/20 disabled:opacity-50 text-[#294a93]
            font-semibold py-2 rounded-xl text-sm transition-colors"
        >
          {adding ? "Adding…" : "+ Add Material"}
        </button>
      </div>
    </div>
  );
}

function GracePeriodManager({ session, exercise, onChanged }) {
  const [hours, setHours]     = useState(24);
  const [busy, setBusy]       = useState(false);
  const [localExercise, setLocalExercise] = useState(exercise);

  // Keep in sync when the parent legitimately swaps sessions/exercises,
  // but our own grant()/clear() responses are the source of truth in between —
  // the parent's background refresh can lag behind (stale `selectedSess`),
  // so we don't want to wait on it to reflect what just happened.
  useEffect(() => { setLocalExercise(exercise); }, [exercise]);

  const graceActive = localExercise?.grace_until && new Date(localExercise.grace_until) > new Date();

  const authHeader = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
  };

  const grant = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.post(`/schedules/${session.id}/exercise/grace`, { hours: Number(hours) });
      if (data?.exercise) setLocalExercise(data.exercise);
      toast.success(`Grace period granted for ${hours}h.`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not grant grace period.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.delete(`/schedules/${session.id}/exercise/grace`);
      if (data?.exercise) setLocalExercise(data.exercise);
      toast.success("Grace period cleared.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not clear grace period.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 bg-amber-50 border border-dashed border-amber-200 rounded-xl p-3">
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Grace Period</p>
      {graceActive ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-amber-800">
            Active until {new Date(localExercise.grace_until).toLocaleString()}
          </p>
          <button type="button" onClick={clear} disabled={busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50">
            {busy ? "…" : "End Grace Period Now"}
          </button>
        </div>
      ) : (
        <div>
          {!localExercise?.due_at && (
            <p className="text-xs text-amber-700 mb-2">
              This assignment has no deadline yet — granting a grace period will close it as of right now and let students submit for the window below.
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={168}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-20 border border-amber-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <span className="text-xs text-amber-700">hours</span>
            <button type="button" onClick={grant} disabled={busy || !hours}
              className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {busy ? "…" : "Grant Grace Period"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AssignmentManager({ session, onChanged }) {
  const exercise = session.exercise;
  const [title,        setTitle]        = useState(exercise?.title || "");
  const [description,  setDescription]  = useState(exercise?.description || "");
  const [releaseAt,    setReleaseAt]    = useState(exercise?.solution_release_at ? exercise.solution_release_at.substring(0, 16) : "");
  const [dueAt,         setDueAt]        = useState(exercise?.due_at ? exercise.due_at.substring(0, 16) : "");
  const [problemFile,  setProblemFile]  = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);
  const [saving,        setSaving]      = useState(false);
  const problemRef  = useRef();
  const solutionRef = useRef();

  useEffect(() => {
    setTitle(exercise?.title || "");
    setDescription(exercise?.description || "");
    setReleaseAt(exercise?.solution_release_at ? exercise.solution_release_at.substring(0, 16) : "");
    setDueAt(exercise?.due_at ? exercise.due_at.substring(0, 16) : "");
    setProblemFile(null);
    setSolutionFile(null);
    if (problemRef.current) problemRef.current.value = "";
    if (solutionRef.current) solutionRef.current.value = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Give the assignment a title."); return; }
    if (!exercise && !problemFile) { toast.error("Upload the assignment file."); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const fd = new FormData();
      fd.append("title", title);
      if (description) fd.append("description", description);
      if (releaseAt) fd.append("solution_release_at", releaseAt);
      if (dueAt) fd.append("due_at", dueAt);
      if (problemFile) fd.append("problem_file", problemFile);
      if (solutionFile) fd.append("solution_file", solutionFile);

      await client.post(`/schedules/${session.id}/exercise`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Assignment saved.");
      setProblemFile(null); setSolutionFile(null);
      if (problemRef.current) problemRef.current.value = "";
      if (solutionRef.current) solutionRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save assignment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Class Assignment</p>

      {exercise && (
        <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3">
          <p className="text-sm font-medium text-gray-800">{exercise.title}</p>
          <a href={exercise.problem_download_link} target="_blank" rel="noreferrer"
            className="text-xs text-[#294a93] hover:underline font-semibold">
            View current assignment file
          </a>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Assignment title, e.g. Week 3 Problem Set"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
            focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description / instructions (optional)"
          rows={3}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
            focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors resize-none"
        />
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            {exercise ? "Replace assignment file (optional)" : "Assignment file"}
          </label>
          <input
            ref={problemRef}
            type="file"
            accept={UPLOAD_ACCEPTED_FILES}
            onChange={(e) => setProblemFile(e.target.files[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3
              file:rounded-lg file:border-0 file:text-xs file:font-semibold
              file:bg-[#294a93]/10 file:text-[#294a93] hover:file:bg-[#294a93]/20 cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Solution file (optional)</label>
          <input
            ref={solutionRef}
            type="file"
            accept={UPLOAD_ACCEPTED_FILES}
            onChange={(e) => setSolutionFile(e.target.files[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3
              file:rounded-lg file:border-0 file:text-xs file:font-semibold
              file:bg-[#294a93]/10 file:text-[#294a93] hover:file:bg-[#294a93]/20 cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Submission deadline (optional)</label>
          <input
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
              focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Solution release date (optional)</label>
          <input
            type="datetime-local"
            value={releaseAt}
            onChange={(e) => setReleaseAt(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
              focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full bg-[#294a93]/10 hover:bg-[#294a93]/20 disabled:opacity-50 text-[#294a93]
            font-semibold py-2 rounded-xl text-sm transition-colors"
        >
          {saving ? "Saving…" : exercise ? "Update Assignment" : "+ Add Assignment"}
        </button>
      </form>

      {exercise && (
        <GracePeriodManager session={session} exercise={exercise} onChanged={onChanged} />
      )}
    </div>
  );
}

function SessionEditForm({ session, onSaved }) {
  const [form, setForm]       = useState({
    topic:          session.topic          || "",
    link:           session.attributes?.link ?? session.link ?? "",
    recording_link: session.recording_link || "",
  });
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    setForm({
      topic:          session.topic          || "",
      link:           session.attributes?.link ?? session.link ?? "",
      recording_link: session.recording_link || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const fd = new FormData();
      fd.append("_method", "PATCH");
      if (form.topic)          fd.append("topic",          form.topic);
      if (form.link)           fd.append("link",           form.link);
      if (form.recording_link) fd.append("recording_link", form.recording_link);

      await client.post(`/schedules/${session.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Session saved.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, placeholder, type = "text") => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
          focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <form onSubmit={handleSave} className="space-y-4">
        {field("Session Topic", "topic", "e.g. Introduction to React Hooks")}
        {field("Meeting Link (live class)", "link", "https://meet.google.com/...", "url")}
        {field("Recording Link (after class)", "recording_link", "https://vimeo.com/...", "url")}

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-50 text-white
            font-semibold py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <MaterialsManager session={session} onChanged={onSaved} />
      <AssignmentManager session={session} onChanged={onSaved} />
    </div>
  );
}

// ─── Comments panel ───────────────────────────────────────────────────────────

function CommentItem({ comment, user, onDelete, onReplyPosted, depth = 0 }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText]  = useState("");
  const [posting,  setPosting]    = useState(false);
  const [deleting, setDeleting]   = useState(false);

  const canDelete =
    user?.id === comment.user_id ||
    user?.is_superadmin         ||
    user?.user_type === 2;

  const handleDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.delete(`/comments/${comment.id}`);
      onDelete(comment.id);
    } catch {
      toast.error("Could not delete comment.");
    } finally {
      setDeleting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const { data } = await client.post(`/schedules/${comment.schedule_id}/comments`, {
        comment: replyText.trim(),
        parent_id: comment.id,
      });
      onReplyPosted(comment.id, data);
      setReplyText("");
      setShowReply(false);
    } catch {
      toast.error("Could not post reply.");
    } finally {
      setPosting(false);
    }
  };

  const displayName = comment.user?.display_name || comment.user?.firstname || "Unknown";
  const role        = comment.user?.role_label;
  const avatar      = comment.user?.profile_img;
  const timeStr     = comment.formatted_time || (comment.created_at ? format(new Date(comment.created_at), "MMM d, h:mm a") : "");

  return (
    <div className={`${depth > 0 ? "ml-8 border-l-2 border-gray-100 pl-4" : ""}`}>
      <div className="flex gap-3 py-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-gray-200">
          {avatar ? (
            <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-500">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5 mb-1">
            <span className="font-semibold text-sm text-gray-800">{displayName}</span>
            {role && (
              <span className="text-xs bg-[#294a93]/10 text-[#294a93] px-1.5 py-0.5 rounded-full font-semibold">
                {role}
              </span>
            )}
            <span className="text-xs text-gray-400">{timeStr}</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{comment.comment}</p>
          <div className="flex items-center gap-3 mt-1.5">
            {depth === 0 && (
              <button
                onClick={() => setShowReply(!showReply)}
                className="text-xs text-[#294a93] hover:underline font-semibold"
              >
                Reply
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs text-rose-500 hover:underline font-semibold disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
          </div>
          {showReply && (
            <form onSubmit={handleReply} className="mt-2 flex gap-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none
                  focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]"
              />
              <button
                type="submit"
                disabled={posting || !replyText.trim()}
                className="bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-50 text-white
                  text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors"
              >
                {posting ? "…" : "Post"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {(comment.replies || []).map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          user={user}
          depth={depth + 1}
          onDelete={(id) => onDelete(id)}
          onReplyPosted={onReplyPosted}
        />
      ))}
    </div>
  );
}

function CommentsPanel({ session, user, onCountUpdate }) {
  const [comments,  setComments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [newText,   setNewText]   = useState("");
  const [posting,   setPosting]   = useState(false);
  const bottomRef                 = useRef();

  const loadComments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const { data } = await client.get(`/schedules/${session.id}/comments`);
      const list = Array.isArray(data) ? data : [];
      setComments(list);
      // Opening the thread marks its comments as read, so the unread badge clears.
      client.post(`/schedules/${session.id}/comments/mark-read`).catch(() => {});
      onCountUpdate?.(session.id, 0);
    } catch {
      toast.error("Could not load comments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newText.trim()) return;
    setPosting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const { data } = await client.post(`/schedules/${session.id}/comments`, {
        comment: newText.trim(),
      });
      data.replies = [];
      setComments((prev) => [...prev, data]);
      setNewText("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Could not post comment.");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = (deletedId) => {
    setComments((prev) =>
      prev
        .filter((c) => c.id !== deletedId)
        .map((c) => ({
          ...c,
          replies: (c.replies || []).filter((r) => r.id !== deletedId),
        }))
    );
  };

  const handleReplyPosted = (parentId, reply) => {
    reply.replies = [];
    setComments((prev) =>
      prev.map((c) =>
        c.id === parentId ? { ...c, replies: [...(c.replies || []), reply] } : c
      )
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-gray-200 border-t-[#294a93] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Comment list */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
        {comments.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-4xl mb-2">💬</p>
            <p className="text-sm font-semibold text-gray-600">No comments yet</p>
            <p className="text-xs text-gray-400 mt-1">Be the first to start the discussion.</p>
          </div>
        ) : (
          comments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              user={user}
              onDelete={handleDelete}
              onReplyPosted={handleReplyPosted}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* New comment box */}
      <form onSubmit={handlePost} className="pt-4 border-t border-gray-100 flex-shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Write a comment to your students…"
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none
              focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
          />
          <button
            type="submit"
            disabled={posting || !newText.trim()}
            className="bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-50 text-white
              font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors"
          >
            {posting ? "…" : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Session detail panel ─────────────────────────────────────────────────────

function SessionDetailPanel({ session, user, onSaved, onCountUpdate }) {
  const [tab, setTab] = useState("manage");

  // Show a "live now" or "join upcoming" banner
  const isLive     = session.status === "live";
  const isUpcoming = session.status === "upcoming";
  const liveLink   = session.attributes?.link ?? session.link ?? null;
  const startDate  = safeDate(session.start_time);

  return (
    <div className="flex flex-col h-full">
      {/* Session header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <StatusBadge status={session.status} />
              {session._week && (
                <span className="text-xs text-gray-400 font-medium">{session._week}</span>
              )}
            </div>
            <h2 className="text-lg font-bold text-gray-800 leading-snug">
              {session.topic || "Untitled Session"}
            </h2>
            {startDate && (
              <p className="text-sm text-gray-500 mt-0.5">
                {format(startDate, "EEEE, MMMM d, yyyy · h:mm a")}
              </p>
            )}
          </div>

          {/* Join button */}
          {(isLive || isUpcoming) && liveLink && (
            <a
              href={liveLink}
              target="_blank"
              rel="noreferrer"
              className={`flex-shrink-0 inline-flex items-center gap-1.5 font-semibold text-sm
                px-4 py-2.5 rounded-xl transition-colors text-white
                ${isLive
                  ? "bg-red-500 hover:bg-red-600 animate-pulse"
                  : "bg-[#294a93] hover:bg-[#1e3a7a]"}`}
            >
              {isLive ? "🔴 Join Live" : "📹 Join Class"}
            </a>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {[
            { key: "manage",   label: "Manage Class" },
            { key: "comments", label: "Comments" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors
                ${tab === t.key
                  ? "bg-[#001931] text-white"
                  : "text-gray-500 hover:bg-gray-100"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {tab === "manage" ? (
          <SessionEditForm session={session} onSaved={onSaved} />
        ) : (
          <CommentsPanel session={session} user={user} onCountUpdate={onCountUpdate} />
        )}
      </div>
    </div>
  );
}

// ─── Add session modal ────────────────────────────────────────────────────────

function AddSessionModal({ modules, cohortId, courseId, onClose, onCreated }) {
  const blank = { topic: "", start_time: "", link: "", description: "", module_id: "", week_id: "", type: "lecture" };
  const [form,    setForm]    = useState(blank);
  const [saving,  setSaving]  = useState(false);

  const selectedModule = modules.find((m) => String(m.id) === String(form.module_id));
  const weeks = selectedModule?.weeks || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.topic || !form.start_time || !form.module_id) {
      toast.error("Topic, date/time and module are required.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const payload = {
        topic:       form.topic,
        start_time:  form.start_time,
        link:        form.link || undefined,
        description: form.description || undefined,
        module_id:   form.module_id,
        week_id:     form.week_id || undefined,
        cohort_id:   cohortId,
        course_id:   courseId,
        type:        form.type,
      };
      await client.post("/schedules", payload);
      toast.success("Session created.");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create session.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">Add New Session</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Topic *</label>
            <input type="text" required value={form.topic} onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
              placeholder="e.g. Introduction to APIs"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Date & Time *</label>
            <input type="datetime-local" required value={form.start_time} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Module *</label>
              <select required value={form.module_id} onChange={(e) => setForm((p) => ({ ...p, module_id: e.target.value, week_id: "" }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]">
                <option value="">Select module</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Week (optional)</label>
              <select value={form.week_id} onChange={(e) => setForm((p) => ({ ...p, week_id: e.target.value }))}
                disabled={weeks.length === 0}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] disabled:opacity-50">
                <option value="">No week</option>
                {weeks.map((w) => <option key={w.id} value={w.id}>{w.name || `Week ${w.number}`}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Meeting Link</label>
            <input type="url" value={form.link} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))}
              placeholder="https://meet.google.com/..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Session Type</label>
            <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]">
              <option value="lecture">Lecture</option>
              <option value="workshop">Workshop</option>
              <option value="qa_session">Q&A Session</option>
              <option value="makeup">Makeup Class</option>
              <option value="other">Other</option>
            </select>
            {form.type === "makeup" && (
              <p className="text-xs text-gray-400 mt-1">Shows on the student dashboard like a normal class, but attendance isn't tracked.</p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-50 text-white text-sm font-semibold">
              {saving ? "Creating…" : "Create Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Project materials manager ─────────────────────────────────────────────────

function ProjectMaterialsManager({ project, onChanged }) {
  const materials = project.materials || [];
  const [newName, setNewName] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef();

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { toast.error("Give the material a name."); return; }
    if (!newFile) { toast.error("Choose a file to upload."); return; }

    setAdding(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const fd = new FormData();
      fd.append("name", newName);
      fd.append("file", newFile);

      await client.post(`/projects/${project.id}/materials`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Material added.");
      setNewName(""); setNewFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add material.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (materialId) => {
    setDeletingId(materialId);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.delete(`/project-materials/${materialId}`);
      toast.success("Material removed.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove material.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Project Files</p>

      {materials.length > 0 && (
        <ul className="space-y-2 mb-4">
          {materials.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <a href={m.url} target="_blank" rel="noreferrer"
                className="text-sm text-[#294a93] hover:underline font-medium truncate">
                {m.name}
              </a>
              <button
                type="button"
                onClick={() => handleDelete(m.id)}
                disabled={deletingId === m.id}
                className="text-xs text-red-500 hover:text-red-700 font-semibold flex-shrink-0 disabled:opacity-50"
              >
                {deletingId === m.id ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="File name, e.g. Starter Repo"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
            focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
        />
        <input
          ref={fileRef}
          type="file"
          accept={UPLOAD_ACCEPTED_FILES}
          onChange={(e) => setNewFile(e.target.files[0] || null)}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3
            file:rounded-lg file:border-0 file:text-xs file:font-semibold
            file:bg-[#294a93]/10 file:text-[#294a93] hover:file:bg-[#294a93]/20 cursor-pointer"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding}
          className="w-full bg-[#294a93]/10 hover:bg-[#294a93]/20 disabled:opacity-50 text-[#294a93]
            font-semibold py-2 rounded-xl text-sm transition-colors"
        >
          {adding ? "Adding…" : "+ Add File"}
        </button>
      </div>
    </div>
  );
}

// ─── Project grace period ─────────────────────────────────────────────────────

function ProjectGracePeriodManager({ project, onChanged }) {
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(project);

  useEffect(() => { setLocal(project); }, [project]);

  const graceActive = local?.grace_until && new Date(local.grace_until) > new Date();

  const authHeader = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
  };

  const grant = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.post(`/projects/${project.id}/grace`, { hours: Number(hours) });
      if (data?.project) setLocal(data.project);
      toast.success(`Grace period granted for ${hours}h.`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not grant grace period.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.delete(`/projects/${project.id}/grace`);
      if (data?.project) setLocal(data.project);
      toast.success("Grace period cleared.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not clear grace period.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 bg-amber-50 border border-dashed border-amber-200 rounded-xl p-3">
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Grace Period</p>
      {graceActive ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-amber-800">
            Active until {new Date(local.grace_until).toLocaleString()}
          </p>
          <button type="button" onClick={clear} disabled={busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50">
            {busy ? "…" : "End Grace Period Now"}
          </button>
        </div>
      ) : (
        <div>
          {!local?.due_at && (
            <p className="text-xs text-amber-700 mb-2">
              This project has no deadline yet — granting a grace period closes it as of now and lets students submit for the window below.
            </p>
          )}
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={168} value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-20 border border-amber-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
            <span className="text-xs text-amber-700">hours</span>
            <button type="button" onClick={grant} disabled={busy || !hours}
              className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {busy ? "…" : "Grant Grace Period"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add / Edit project modal ─────────────────────────────────────────────────

function AddEditProjectModal({ modules, project, onClose, onSaved }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name:         project?.name         || project?.title || "",
    description:  project?.description  || "",
    instructions: project?.instructions || "",
    due_at:       project?.due_at       ? project.due_at.substring(0, 16) : "",
    module_id:    project?.module_id    || "",
  });
  const [saving, setSaving] = useState(false);
  const [materials, setMaterials] = useState(project?.materials || []);
  const [newMaterialName, setNewMaterialName] = useState("");
  const [newMaterialFile, setNewMaterialFile] = useState(null);

  const refreshMaterials = async () => {
    if (!isEdit) return;
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const { data } = await client.get(`/projects/${project.id}/materials`);
      setMaterials(data?.materials || []);
    } catch {
      // keep showing the last known list if the refresh fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || (!isEdit && !form.module_id)) {
      toast.error("Project name and module are required.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      if (isEdit) {
        // null (not undefined) so a cleared field is actually cleared server-side.
        await client.patch(`/projects/${project.id}`, {
          name:         form.name,
          description:  form.description  || null,
          instructions: form.instructions || null,
          due_at:       form.due_at       || null,
        });
        toast.success("Project updated.");
      } else {
        const { data } = await client.post("/projects", {
          name:         form.name,
          description:  form.description  || undefined,
          instructions: form.instructions || undefined,
          due_at:       form.due_at       || undefined,
          module_id:    form.module_id,
        });

        if (newMaterialFile && data?.project?.id) {
          try {
            const fd = new FormData();
            fd.append("name", newMaterialName.trim() || newMaterialFile.name);
            fd.append("file", newMaterialFile);
            await client.post(`/projects/${data.project.id}/materials`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (fileErr) {
            toast.error(fileErr?.response?.data?.message || "Project created, but the file could not be attached.");
          }
        }

        toast.success("Project created.");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save project.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-800">{isEdit ? "Edit Project" : "Add New Project"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Project Name *</label>
            <input type="text" required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Build a REST API"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          {!isEdit && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Module *</label>
              <select required value={form.module_id} onChange={(e) => setForm((p) => ({ ...p, module_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]">
                <option value="">Select module</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="What is this project about?"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Instructions</label>
            <textarea rows={4} value={form.instructions} onChange={(e) => setForm((p) => ({ ...p, instructions: e.target.value }))}
              placeholder="Step-by-step instructions for students…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Due Date (optional)</label>
            <input type="datetime-local" value={form.due_at} onChange={(e) => setForm((p) => ({ ...p, due_at: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
            {isEdit && <ProjectGracePeriodManager project={project} onChanged={onSaved} />}
          </div>
          {isEdit ? (
            <ProjectMaterialsManager project={{ id: project.id, materials }} onChanged={refreshMaterials} />
          ) : (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Project File (optional)</p>
              <div className="space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
                <input
                  type="text"
                  value={newMaterialName}
                  onChange={(e) => setNewMaterialName(e.target.value)}
                  placeholder="File name, e.g. Starter Repo"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none
                    focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93] transition-colors"
                />
                <input
                  type="file"
                  accept={UPLOAD_ACCEPTED_FILES}
                  onChange={(e) => setNewMaterialFile(e.target.files[0] || null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3
                    file:rounded-lg file:border-0 file:text-xs file:font-semibold
                    file:bg-[#294a93]/10 file:text-[#294a93] hover:file:bg-[#294a93]/20 cursor-pointer"
                />
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-50 text-white text-sm font-semibold">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Projects manager ─────────────────────────────────────────────────────────

function ProjectsManager({ modules, onAddProject, onEditProject, onProjectDeleted }) {
  const projects = modules.flatMap((m) =>
    (m.projects || []).map((p) => ({ ...p, _module: m.name }))
  );
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.title || project.name}"? This cannot be undone.`)) return;
    setDeletingId(project.id);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.delete(`/projects/${project.id}`);
      toast.success("Project deleted.");
      onProjectDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete project.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="font-bold text-gray-800">{projects.length} project{projects.length !== 1 ? "s" : ""}</p>
        <button
          onClick={onAddProject}
          className="flex items-center gap-1.5 bg-[#294a93] hover:bg-[#1e3a7a] text-white
            text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
        >
          + Add Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-5xl mb-3">📋</p>
          <p className="font-semibold text-gray-700">No projects yet</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">Create your first project for students.</p>
          <button
            onClick={onAddProject}
            className="bg-[#294a93] hover:bg-[#1e3a7a] text-white text-sm font-semibold px-4 py-2.5 rounded-xl"
          >
            + Add Project
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const total   = p.submissions_count ?? 0;
            const graded  = p.graded_count       ?? 0;
            const pending = total - graded;
            return (
              <div key={p.id} className="bg-gray-50 border border-gray-100 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-[#294a93]/10 flex items-center justify-center text-base flex-shrink-0">📁</div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 text-sm leading-snug">{p.title || p.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{p._module}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1.5">
                    <button
                      onClick={() => onEditProject(p)}
                      className="text-xs bg-white border border-gray-200 hover:bg-gray-100
                        text-gray-600 font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      disabled={deletingId === p.id}
                      className="text-xs bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200
                        text-red-500 font-semibold px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deletingId === p.id ? "…" : "🗑️"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Submitted", value: total,   color: "text-gray-800" },
                    { label: "Graded",    value: graded,  color: "text-green-600" },
                    { label: "Pending",   value: pending, color: pending > 0 ? "text-amber-600" : "text-gray-400" },
                  ].map((s) => (
                    <div key={s.label} className="bg-white rounded-xl p-2 text-center">
                      <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-400">{s.label}</p>
                    </div>
                  ))}
                </div>

                {p.description && (
                  <p className="text-xs text-gray-400 mt-3 leading-relaxed line-clamp-2">
                    {String(p.description).replace(/<[^>]*>/g, " ").trim()}
                  </p>
                )}
                {p.due_at && (
                  <p className="text-xs text-[#294a93] mt-2 font-medium">
                    Due: {format(new Date(p.due_at), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function InstructorBlackboard({ blackboard, user, cohortId, onRefresh }) {
  const [mainTab,          setMainTab]          = useState("sessions");
  const [selectedSess,     setSelectedSess]     = useState(null);
  const [commentCounts,    setCommentCounts]    = useState({});
  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [addSessionOpen,   setAddSessionOpen]   = useState(false);
  const [addProjectOpen,   setAddProjectOpen]   = useState(false);
  const [editProject,      setEditProject]      = useState(null);

  const { modules = [], course } = blackboard;
  const courseId = course?.id;
  const sessions = extractSessions(modules);

  // Auto-select first session
  useEffect(() => {
    if (sessions.length > 0 && !selectedSess) {
      // prefer live, else first
      const live = sessions.find((s) => s.status === "live");
      setSelectedSess(live || sessions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules.length]);

  // Seed per-session badges with unread comment counts (not totals)
  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/notifications/unread-comment-counts")
      .then(({ data }) => {
        setCommentCounts((prev) => ({ ...prev, ...data }));
      })
      .catch(() => {});
  }, []);

  const handleCountUpdate = (scheduleId, count) => {
    setCommentCounts((prev) => ({ ...prev, [scheduleId]: count }));
  };

  const handleSelectSession = (s) => {
    setSelectedSess(s);
    setMobileOpen(false);
  };

  // Stats
  const totalSessions = sessions.length;
  const liveSessions  = sessions.filter((s) => s.status === "live").length;
  const upcoming      = sessions.filter((s) => s.status === "upcoming").length;
  const recorded      = sessions.filter((s) => s.status === "recorded").length;

  return (
    <div className="flex flex-col h-full">
      {/* ── Stats bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Total Sessions",  value: totalSessions, color: "text-[#001931]",  bg: "bg-white" },
          { label: "Recorded",        value: recorded,      color: "text-green-600",  bg: "bg-white" },
          { label: "Upcoming",        value: upcoming,      color: "text-blue-600",   bg: "bg-white" },
          { label: "Live Now",        value: liveSessions,  color: "text-red-500",    bg: "bg-white" },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl border border-gray-100 p-4 text-center shadow-sm`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Top tabs + action buttons ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "sessions",  label: "Sessions" },
          { key: "projects",  label: "Projects" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setMainTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors
              ${mainTab === t.key
                ? "bg-[#001931] text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            {t.label}
          </button>
        ))}

        {/* Action button changes based on active tab */}
        {mainTab === "sessions" && (
          <button
            onClick={() => setAddSessionOpen(true)}
            className="ml-auto flex items-center gap-1.5 bg-[#d15424] hover:bg-[#b8451d]
              text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
          >
            + Add Session
          </button>
        )}

        {/* Mobile: open session picker */}
        {mainTab === "sessions" && (
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden bg-white border border-gray-200 text-gray-700
              px-3 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50"
          >
            ☰ Sessions
          </button>
        )}
      </div>

      {/* ── Modals ── */}
      {addSessionOpen && (
        <AddSessionModal
          modules={modules}
          cohortId={cohortId}
          courseId={courseId}
          onClose={() => setAddSessionOpen(false)}
          onCreated={() => { onRefresh?.(); setAddSessionOpen(false); }}
        />
      )}
      {(addProjectOpen || editProject) && (
        <AddEditProjectModal
          modules={modules}
          project={editProject || null}
          onClose={() => { setAddProjectOpen(false); setEditProject(null); }}
          onSaved={() => { onRefresh?.(); setAddProjectOpen(false); setEditProject(null); }}
        />
      )}

      {/* ── Content ── */}
      {mainTab === "projects" ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <ProjectsManager
            modules={modules}
            onAddProject={() => setAddProjectOpen(true)}
            onEditProject={(p) => setEditProject(p)}
            onProjectDeleted={() => onRefresh?.()}
          />
        </div>
      ) : (
        /* Sessions panel */
        <div className="flex bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
          style={{ minHeight: "600px" }}>

          {/* Desktop session sidebar */}
          <div className="hidden lg:flex">
            <SessionSidebar
              sessions={sessions}
              selectedId={selectedSess?.id}
              onSelect={handleSelectSession}
              commentCounts={commentCounts}
            />
          </div>

          {/* Mobile session drawer */}
          {mobileOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black/60"
                onClick={() => setMobileOpen(false)}
              />
              <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-2xl overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="font-bold text-gray-800 text-sm">Sessions</p>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-sm"
                  >
                    ✕
                  </button>
                </div>
                <SessionSidebar
                  sessions={sessions}
                  selectedId={selectedSess?.id}
                  onSelect={handleSelectSession}
                  commentCounts={commentCounts}
                />
              </div>
            </div>
          )}

          {/* Main panel */}
          <div className="flex-1 min-w-0">
            {selectedSess ? (
              <SessionDetailPanel
                key={selectedSess.id}
                session={selectedSess}
                user={user}
                onSaved={onRefresh}
                onCountUpdate={handleCountUpdate}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6">
                <p className="text-5xl mb-4">📅</p>
                <p className="font-bold text-gray-700 text-lg">Select a session</p>
                <p className="text-sm text-gray-400 mt-1">
                  Choose a session from the left to manage it.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
